import { Box } from '../../atoms/Box/index'
import { DrawerComponentProps } from '../../../../src/types/organisms/drawer.types'
import { IconButton } from '../../atoms/Icon/IconButton'
import { PositionProps, ZIndexProps, zIndex } from 'styled-system'
import { ReactPortal, useEffect, useRef, useState } from 'react'
import { useIsMounted } from '@dtx-company/true-common/src/hooks/useIsMounted'
import OutsideClickHandler from 'react-outside-click-handler'
import ReactDOM from 'react-dom'
import closeIcon from '../../../static/icons/close-dark.svg'
import styled from 'styled-components'

const DrawerAside = styled.aside<{ delay: number; bg: string } & ZIndexProps>`
  position: fixed;
  overflow-y: scroll;
  top: 0;
  width: 100%;
  max-width: 100%;
  height: 100%;
  background-color: ${({ bg }: { bg: string }) => bg};
  transition: ${({ delay }: { delay: number }) => `${delay}ms background-color`};
  z-index: ${({ theme }) => theme.zIndex.aside};
  ${zIndex}
`

const DrawerWrapper = styled(Box)`
  transition: ${({ delay }: { delay: number }) => `${delay}ms all`};
`

export const Drawer = ({
  children,
  open,
  onClose,
  delay = 250,
  zIndex,
  disableAutoClose,
  useCloseButton = true,
  usingPortal = true,
  ...rest
}: DrawerComponentProps): ReactPortal | JSX.Element | null => {
  const isMounted = useIsMounted()
  const drawerRef = useRef<HTMLDivElement>(null)
  const [leftPos, setLeftPos] = useState<PositionProps['left']>({ left: '100%' })
  const [bg, setBg] = useState<string>('none')
  const [portalOpen, setPortalOpen] = useState<boolean>(open)

  useEffect(() => {
    let timeoutHandler: ReturnType<typeof setTimeout>
    if (open) {
      setPortalOpen(true)
      timeoutHandler = setTimeout(() => {
        setLeftPos({ left: 0 })
        setBg('rgba(0, 0, 0, 0.8)')
      }, 100)
    }
    if (!open && portalOpen) {
      setLeftPos({ left: '100%' })
      setBg('none')
      timeoutHandler = setTimeout(() => {
        setPortalOpen(false)
      }, delay - 50)
    }
    return function cleanup() {
      clearTimeout(timeoutHandler)
    }
  }, [open, portalOpen, delay])

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && open) {
        onClose()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return function cleanup() {
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [onClose, open])

  const Content = (
    <DrawerAside zIndex={zIndex} delay={delay} bg={bg}>
      <DrawerWrapper
        ref={drawerRef}
        left={leftPos}
        delay={delay}
        justifyContent={'flex-end'}
        position="absolute"
        width="100%"
        top={0}
        bottom={0}
      >
        <OutsideClickHandler
          onOutsideClick={() => {
            if (!disableAutoClose) {
              onClose()
            }
          }}
        >
          <Box
            position="relative"
            backgroundColor="primary.white"
            minHeight="100%"
            width="100%"
            {...rest}
          >
            {useCloseButton ? (
              <IconButton
                tabIndex={0}
                position={'absolute'}
                top={'24px'}
                right={'24px'}
                src={closeIcon}
                onClick={() => {
                  onClose()
                }}
              />
            ) : (
              <></>
            )}
            {children}
          </Box>
        </OutsideClickHandler>
      </DrawerWrapper>
    </DrawerAside>
  )

  if (isMounted()) {
    if (open && !usingPortal) return Content
    if (portalOpen) return ReactDOM.createPortal(Content, document.body)
  }
  return null
}
